import { Reveal } from "@/components/motion/Reveal";
import { CalendarCheck, DollarSign, MessageSquare, TrendingUp, Circle } from "lucide-react";

interface Kpi {
  label: string;
  value: string;
  change: string;
  icon: React.ComponentType<{ className?: string }>;
}

const KPIS: Kpi[] = [
  { label: "Meetings this month", value: "14", change: "+6 vs last mo", icon: CalendarCheck },
  { label: "Pipeline attributed", value: "$182K", change: "3 deals in progress", icon: DollarSign },
  { label: "Positive reply rate", value: "4.8%", change: "Above SaaS avg", icon: TrendingUp },
  { label: "AM response time", value: "42m", change: "Sarah on it", icon: MessageSquare },
];

export function PortalShowcase({
  eyebrow = "YOUR CLIENT PORTAL",
  headline = "Log in anytime. See every meeting, every reply, every dollar of pipeline.",
  sub = "You don't operate the platform. You watch the output.",
}: {
  eyebrow?: string;
  headline?: string;
  sub?: string;
}) {
  return (
    <section className="relative">
      <div className="max-w-6xl mx-auto px-4 py-20 md:py-28">
        <Reveal stagger={60} className="text-center space-y-3 max-w-2xl mx-auto mb-10">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary">{eyebrow}</p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">{headline}</h2>
          <p className="text-sm md:text-base text-foreground/80">{sub}</p>
        </Reveal>

        <Reveal delay={200}>
          <div className="relative rounded-2xl border border-border/60 bg-background/70 backdrop-blur-xl overflow-hidden shadow-2xl shadow-primary/10">
            {/* Browser chrome */}
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border/50 bg-card/40">
              <Circle className="h-2.5 w-2.5 fill-foreground/25 text-foreground/25" />
              <Circle className="h-2.5 w-2.5 fill-foreground/25 text-foreground/25" />
              <Circle className="h-2.5 w-2.5 fill-foreground/25 text-foreground/25" />
              <div className="ml-4 flex-1 max-w-md">
                <div className="h-6 rounded-md bg-background/60 border border-border/40 flex items-center px-3 text-[10px] font-mono text-foreground/60">
                  app.autopitch.io / dashboard
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] min-h-[420px]">
              {/* Sidebar mock */}
              <div className="hidden md:flex flex-col gap-1 p-4 border-r border-border/50 bg-card/20">
                {["Dashboard", "Meetings", "Performance", "Messages", "Reports", "Settings"].map(
                  (item, i) => (
                    <div
                      key={item}
                      className={`text-xs px-3 py-2 rounded-lg font-medium ${
                        i === 0
                          ? "bg-primary/15 text-primary"
                          : "text-foreground/60"
                      }`}
                    >
                      {item}
                    </div>
                  ),
                )}
              </div>

              {/* Content mock */}
              <div className="p-5 md:p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                      Managed by Sarah · Account Manager
                    </p>
                    <h3 className="text-lg font-semibold text-foreground mt-1">
                      This month, on your behalf
                    </h3>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-success/10 border border-success/20">
                    <span className="h-1.5 w-1.5 rounded-full bg-success" />
                    <span className="text-[10px] font-mono tracking-widest text-success/90">
                      SENDING · LIVE
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {KPIS.map((k) => (
                    <div
                      key={k.label}
                      className="rounded-xl border border-border/50 bg-card/40 p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-mono uppercase tracking-widest text-foreground/55">
                          {k.label}
                        </span>
                        <k.icon className="h-3 w-3 text-primary" />
                      </div>
                      <p className="text-2xl font-bold tabular-nums text-foreground">
                        {k.value}
                      </p>
                      <p className="text-[10px] text-foreground/60">{k.change}</p>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-border/50 bg-card/30 p-4 space-y-2.5">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-foreground/55">
                    Recent meetings booked
                  </p>
                  {[
                    { name: "Alex Kim · Head of Ops, Klariq", when: "Thu 2:00pm · confirmed" },
                    { name: "Priya Shah · VP Revenue, Northwind", when: "Fri 10:30am · confirmed" },
                    { name: "Jordan Lee · Founder, Stackly", when: "Mon 4:00pm · awaiting host" },
                  ].map((m) => (
                    <div key={m.name} className="flex items-center justify-between text-xs">
                      <span className="text-foreground/85">{m.name}</span>
                      <span className="text-foreground/55 font-mono">{m.when}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <p className="text-center text-xs text-foreground/55 mt-6 max-w-md mx-auto">
          Composed mock. Real portal shows your actual meetings, replies, and account manager.
        </p>
      </div>
    </section>
  );
}
